// Iter43 — Parity Health Dashboard tile.
//
// Read-only aggregator that pulls `/api/forge/parity-health` and shows
// the WES engine state across the user's active sources. Helps the user
// spot at a glance whether any subsystem is silently degrading. Does
// NOT alter scanner logic — strictly a visual rollup.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, AlertTriangle, Activity, ShieldAlert } from "lucide-react";
import { API } from "@/App";

const PALETTE = {
  green:   "bg-[#00FF88]/10 border-[#00FF88]/40 text-[#00FF88]",
  amber:   "bg-[#FFB800]/10 border-[#FFB800]/40 text-[#FFB800]",
  red:     "bg-[#FF3366]/10 border-[#FF3366]/40 text-[#FF3366]",
  neutral: "bg-[#0A0A0E] border-[#26262B] text-[#8A8A93]",
};

const ICONS = {
  green:   <ShieldCheck size={16} />,
  amber:   <AlertTriangle size={16} />,
  red:     <ShieldAlert size={16} />,
  neutral: <Activity size={16} />,
};

const STATUS_LABEL = {
  OK:     "Healthy",
  STALE:  "Stale rows present",
  ERR:    "Errors detected",
  LEGACY: "Legacy diagnostic detected",
  EMPTY:  "No WES rows yet",
};

const COUNTER_META = [
  { key: "parity",  label: "PARITY",  cls: "text-[#00FF88]" },
  { key: "stale",   label: "STALE",   cls: "text-[#FFB800]" },
  { key: "err",     label: "ERR",     cls: "text-[#FF3366]" },
  { key: "legacy",  label: "LEGACY",  cls: "text-[#FFB800]" },
  { key: "unknown", label: "UNKNOWN", cls: "text-[#FF3366]" },
];

export default function ParityHealthTile() {
  const [data, setData]       = useState(null);
  const [err, setErr]         = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/forge/parity-health`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);  // refresh every 30s
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div data-testid="parity-health-tile-loading"
           className="px-4 py-2 mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E] text-[10px] font-mono text-[#5F5F66]">
        Parity Health · loading…
      </div>
    );
  }
  if (err) {
    return (
      <div data-testid="parity-health-tile-error"
           className="px-4 py-2 mb-5 rounded-sm border border-[#FF3366]/30 bg-[#FF3366]/5 text-[10px] font-mono text-[#FF3366]">
        Parity Health · {err}
      </div>
    );
  }

  const color = data?.color || "neutral";
  const tooltip = data?.message ||
    "WES Parity Engine v1 is the official operational source. ERR/UNKNOWN rows should not be used for WES-based decisions.";

  return (
    <div
      data-testid="parity-health-tile"
      data-status={data?.status}
      data-color={color}
      title={tooltip}
      className={`flex items-center gap-3 px-4 py-2.5 mb-5 rounded-sm border ${PALETTE[color]}`}
    >
      <div className="flex items-center gap-1.5">
        {ICONS[color]}
        <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
          Parity Health
        </span>
        <span data-testid="parity-health-status"
              className="text-[9px] font-mono uppercase tracking-[0.12em] opacity-80 ml-1">
          · {STATUS_LABEL[data?.status] || data?.status}
        </span>
      </div>

      <div className="flex items-center gap-3 ml-auto text-[10px] font-mono">
        {COUNTER_META.map(c => (
          <div
            key={c.key}
            data-testid={`parity-health-count-${c.key}`}
            className={`flex items-center gap-1 ${c.cls}`}
          >
            <span className="opacity-70">{c.label}</span>
            <span className="font-bold">{data?.[c.key] ?? 0}</span>
          </div>
        ))}
        <div data-testid="parity-health-total"
             className="flex items-center gap-1 text-[#8A8A93] pl-2 border-l border-[#26262B]">
          <span className="opacity-70">TOTAL</span>
          <span className="font-bold">{data?.total ?? 0}</span>
        </div>
      </div>
    </div>
  );
}
